/**
 * PlanSheet — right-side slide-over for create / edit plan
 *
 * Wraps PlanForm with a sticky header that shows:
 *   - Gradient icon + plan title + subtitle
 *   - Read-only Helcim sync status badge (auto-syncs, no manual action needed)
 * Errors from the store (HELCIM_SYNC_FAILED, etc.) surface as inline banners.
 */
import { CreditCard, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@respectly/ui';
import { PlanForm } from './PlanForm';
import type { SuperAdminPlan, CreatePlanDto } from '@respectly/shared-types';

// ─── Types ───────────────────────────────────────────────────────────────────

interface PlanSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan?: SuperAdminPlan;
  onSubmit: (dto: CreatePlanDto) => Promise<boolean>;
  isLoading?: boolean;
  actionError?: { code: string; message: string } | null;
}

// ─── Helcim Status Badge ──────────────────────────────────────────────────────

function HelcimStatusBadge({ plan }: { plan?: SuperAdminPlan }) {
  // Create mode
  if (!plan) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1 text-xs font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
        <Clock className="h-3 w-3 shrink-0" />
        Will sync to Helcim on save
      </span>
    );
  }

  // Synced paid plan
  if (plan.helcimPlanId) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
        <CheckCircle2 className="h-3 w-3 shrink-0" />
        Synced to Helcim
      </span>
    );
  }

  // Free tier (monthlyPrice == 0 or falsy)
  if (!Number(plan.monthlyPrice)) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-1 text-xs font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
        <Clock className="h-3 w-3 shrink-0" />
        Free tier — no Helcim sync
      </span>
    );
  }

  // Paid tier but not yet synced (legacy plan)
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-3 py-1 text-xs font-medium text-amber-700 dark:text-amber-400 whitespace-nowrap">
      <AlertTriangle className="h-3 w-3 shrink-0" />
      Not synced — will sync on next save
    </span>
  );
}

// ─── Error Banner ─────────────────────────────────────────────────────────────

function ErrorBanner({ error }: { error: { code: string; message: string } }) {
  const message =
    error.code === 'HELCIM_SYNC_FAILED'
      ? 'Could not connect to Helcim. Plan was not saved. Check Helcim configuration and try again.'
      : error.code === 'HELCIM_NOT_CONFIGURED'
        ? 'Helcim credentials are not configured. Free-tier plans can be saved without Helcim.'
        : error.message;

  return (
    <div className="mx-6 mt-5 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 px-4 py-3">
      <p className="text-sm font-medium text-red-700 dark:text-red-400">{message}</p>
    </div>
  );
}

// ─── PlanSheet ────────────────────────────────────────────────────────────────

export function PlanSheet({
  open,
  onOpenChange,
  plan,
  onSubmit,
  isLoading,
  actionError,
}: PlanSheetProps) {
  const isEditing = !!plan;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-[min(700px,100vw)] p-0 flex flex-col overflow-hidden border-l border-slate-200 dark:border-slate-700"
      >
        {/* ── Sticky Header ──────────────────────────────────────────── */}
        <SheetHeader className="shrink-0 px-6 pt-6 pb-5 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
          <div className="flex items-start justify-between gap-4">
            {/* Left: icon + title */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm shrink-0">
                <CreditCard className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <SheetTitle className="text-base font-semibold text-slate-900 dark:text-white leading-tight truncate">
                  {isEditing ? plan.name : 'New Plan'}
                </SheetTitle>
                <SheetDescription className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {isEditing ? 'Edit plan details' : 'Create a new subscription plan'}
                </SheetDescription>
              </div>
            </div>

            {/* Right: Helcim status badge */}
            <div className="shrink-0 pt-0.5">
              <HelcimStatusBadge plan={plan} />
            </div>
          </div>
        </SheetHeader>

        {/* ── Scrollable Body ────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-950">
          {actionError && <ErrorBanner error={actionError} />}

          <div className="p-6">
            <PlanForm
              plan={plan}
              onSubmit={onSubmit}
              onCancel={() => onOpenChange(false)}
              isLoading={isLoading}
            />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
